import { Checkbox } from '@hologram-dimension/checkbox';
import { InlineNotification } from '@hologram-dimension/inline-notification';
import useSimStatuses from 'api/sims/useSimStatuses';
import isArray from 'lodash/isArray';
import { type ChangeEventHandler, type FC, useCallback, useMemo } from 'react';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import type { EnumsSimStatus } from 'types/__generated__/SimInventoryApi';
import { filterLabels } from 'components/SimInventoryFilters/shared';
import FilterAccordion, { type FilterProps } from '../../FilterAccordion/FilterAccordion';
import styles from './SimStatusFilter.module.css';

const networkStatusSort = new Set([
  'Connected',
  'Ready / Connected', // TODO: Remove
  'In testing / Connected', // TODO: Remove
  'Ready',
  'In testing',
  'Pending',
  'Entering testing',
  'Ready to activate',
  'Activating',
  'Pausing',
  'Paused by user',
  'Paused by system',
  'Resuming',
  'Deactivating',
  'Available',
] as Array<EnumsSimStatus>);

interface SimStatusFilterProps extends FilterProps {
  selectedOptions?: Array<EnumsSimStatus>;
}

// TODO: Halfway through moving all the status options here, realized that not only is the style duplicated, so is most of the logic
// Consider consolidating this with SimTagFilter unless we anticipate significant differences. E.g. a search field (which is actually.. just an optional thing on a shared control)

const SimStatusFilter: FC<SimStatusFilterProps> = ({ onClear, segmentName, submit, form }) => {
  const { register } = form;
  const storedFilters = useSimInventoryStoredFilters();
  const storedSimStatuses = storedFilters?.simStatuses;

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(() => {
    submit();
  }, [submit]);

  const checkboxField = register('simStatuses', {
    onChange: handleChange,
  });

  const { networkStatusGroups, formatStatus, isLoading, error } = useSimStatuses();

  const statusOptions: Array<EnumsSimStatus> = useMemo(() => {
    let dbOptions;
    switch (segmentName) {
      case 'active':
        dbOptions = networkStatusGroups?.active;
        break;
      case 'preflight':
        dbOptions = networkStatusGroups?.predeployment;
        break;
      case 'deactivated':
        dbOptions = networkStatusGroups?.deactivated;
        break;
      case 'all':
      default:
        dbOptions = [
          ...(networkStatusGroups?.active ?? []),
          ...(networkStatusGroups?.predeployment ?? []),
          ...(networkStatusGroups?.deactivated ?? []),
        ];
    }

    const friendlyOptions = new Set<EnumsSimStatus>();
    (dbOptions ?? []).forEach((internalValue) => {
      const formattedValue = formatStatus(internalValue);

      // TODO: Remove with real connected status
      if (formattedValue === 'Ready') {
        friendlyOptions.add('Ready / Connected' as any);
      } else if (formattedValue === 'In testing') {
        friendlyOptions.add('In testing / Connected' as any);
      } else {
        friendlyOptions.add(formattedValue);
      }
    });

    // Use values found in our sorted filter, removing them from the API values we got as we go. The remainder of the API values will go at the end
    const sorted = new Set([...networkStatusSort].filter((value) => friendlyOptions.delete(value)));

    // Make sure to include the remaining values
    return [...sorted, ...friendlyOptions];
  }, [formatStatus, networkStatusGroups, segmentName]);

  const isSet = !!storedSimStatuses && isArray(storedSimStatuses) && storedSimStatuses.length > 0;

  let content;

  if (error) {
    content = (
      <InlineNotification variant="system-error" style={{ display: 'inline' }}>
        We ran into an issue loading this filter. You can try to refresh.
      </InlineNotification>
    );
    // eslint-disable-next-line no-console
    console.error(error);
  } else if (isLoading) {
    content = <div className={styles.loadingText}>Loading...</div>;
  } else {
    content = (
      <div className={styles.optionWrapper}>
        {statusOptions.map((option) => {
          // Remove replacers when CONNECTED is real
          const value = option
            .replace('Ready / Connected', 'Ready')
            .replace('In testing / Connected', 'In testing');

          return (
            <Checkbox
              className={styles.statusOption}
              label={option}
              title={option}
              value={value}
              key={option}
              defaultChecked={isSet && storedSimStatuses.includes(value as EnumsSimStatus)}
              {...checkboxField}
            />
          );
        })}
      </div>
    );
  }

  return (
    <FilterAccordion onClear={onClear} label={filterLabels.simStatuses} isSet={isSet} defaultActive>
      <div className={styles.wrapper}>{content}</div>
    </FilterAccordion>
  );
};

export default SimStatusFilter;
